import { MutableRefObject, useMemo, useRef, useState } from 'react';
import { ArrayUtils } from '../../../util/array';
import { QNear, Waypoint } from '../../../util/machine-variable.utils';
import { RealtimePositionUtils } from '../../../util/realtime';
import {
  RegisterAndSubscribeToMachineBindingDocument,
  RegisterAndSubscribeToMachineBindingSubscriptionResult,
  RegisterAndSubscribeToMachineBindingSubscriptionVariables,
} from '../../graphql/generated';
import { useApolloSharedSubscription } from '../../subscription/apollo-subscription.manager';
import { MachineBindingResult } from './binding.types';

export interface RealtimePositionResult {
  isInMotion: boolean;
  getLastPosition(): RealtimePositionEvent | null;
}

export interface RealtimePositionConfig {
  rate?: number;
}

export interface RealtimePositionContext {
  rate?: number;
}

export interface RealtimePositionEvent {
  waypoint: Waypoint;
  jointPositions: QNear;
  tcpSpeed?: number[];
}

const MAX_PREVIOUS_RESULTS = 1;

export const useRealtimePositionMonitor = (
  machineId: string,
  config: RealtimePositionConfig
): RealtimePositionResult => {
  const { rate } = config;

  const previousResults: MutableRefObject<
    Array<MachineBindingResult<RealtimePositionEvent>>
  > = useRef([]);

  const [isInMotion, setIsInMotion] = useState<boolean>(false);

  const variables: RegisterAndSubscribeToMachineBindingSubscriptionVariables = {
    input: {
      machineId,
      binding: {
        namespace: 'machine',
        type: 'realtimePositionMonitor',
        context: {
          rate: rate ?? 500,
        },
      },
    },
  };

  useApolloSharedSubscription<RegisterAndSubscribeToMachineBindingSubscriptionResult>(
    {
      query: RegisterAndSubscribeToMachineBindingDocument,
      variables,
      subscriptionName: `binding-machine-realtimePositionMonitor=${machineId}`,
      callbackOnly: true,
      callback: (
        result: RegisterAndSubscribeToMachineBindingSubscriptionResult
      ) => {
        const bindingResult = result.data
          ?.registerAndSubscribeToMachineBindingResult as MachineBindingResult<RealtimePositionEvent>;

        if (bindingResult) {
          const motion: boolean = RealtimePositionUtils.isInMotion(
            bindingResult.data.tcpSpeed || [],
            previousResults.current.map((r) => r?.data?.tcpSpeed || [])
          );

          ArrayUtils.unshift(
            previousResults.current,
            bindingResult,
            MAX_PREVIOUS_RESULTS
          );

          setIsInMotion(motion);
        }
      },
    }
  );

  const result: RealtimePositionResult = useMemo(() => {
    return {
      isInMotion,
      getLastPosition: () =>
        previousResults && previousResults.current
          ? previousResults.current[0]?.data
          : null,
    };
  }, [isInMotion]);

  return result;
};
